import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles.css';

import { API_URL } from '../config';

function ResetPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);

  // Check for reset token in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setIsResetMode(true);
    }
  }, []);

  const handleResetRequest = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      await axios.post(`${API_URL}/api/reset-password`, { email: email.trim() });
      setMessage('Password reset instructions have been sent to your email address. Please check your inbox and spam folder.');
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!newPassword.trim()) {
      setError('New password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      await axios.post(`${API_URL}/api/reset-password/${resetToken}`, {
        newPassword: newPassword.trim()
      });
      setMessage('Password has been reset successfully! You can now login with your new password.');
      setNewPassword('');
      setConfirmPassword('');
      setIsResetMode(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. The link may have expired or is invalid.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {!isResetMode ? (
        <>
          <h1>Reset Your Password</h1>
          <p style={{ marginBottom: '20px', color: '#6c757d', textAlign: 'center' }}>
            Enter your email address and we'll send you instructions to reset your password.
          </p>
          
          {error && <p className="error">{error}</p>}
          {message && <p className="success">{message}</p>}
          
          <form onSubmit={handleResetRequest}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '16px',
                marginBottom: '16px'
              }}
            />
            <button 
              type="submit" 
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: isLoading ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Sending...' : 'Send Reset Instructions'}
            </button>
          </form>
        </>
      ) : (
        <>
          <h1>Set New Password</h1>
          <p style={{ marginBottom: '20px', color: '#6c757d', textAlign: 'center' }}>
            Enter your new password below.
          </p>
          
          {error && <p className="error">{error}</p>}
          {message && <p className="success">{message}</p>}
          
          <form onSubmit={handlePasswordReset}>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '16px',
                marginBottom: '16px'
              }}
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '16px',
                marginBottom: '16px'
              }}
            />
            <button 
              type="submit" 
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: isLoading ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </>
      )}
      
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <p>
          <a 
            href="/login" 
            onClick={(e) => { e.preventDefault(); window.location.href = '/login'; }}
            style={{ color: '#007bff', textDecoration: 'none' }}
          >
            ← Back to Login
          </a>
        </p>
      </div>
    </div>
  );
}

export default ResetPassword;